import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import LandingPage from './screens/LandingPage';
import QuestionScreen from './screens/QuestionScreen';
import IntermediateScreen from './screens/IntermediateScreen';
import AnalysisScreen from './screens/AnalysisScreen';
import ResultsScreen from './screens/ResultsScreen';
import OfferScreen from './screens/OfferScreen';

const Container = styled.div`
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  background-color: ${props => props.theme.colors.background};
`;

const Footer = styled.footer`
  width: 100%;
  background: #f8f2ed;
  color: #6C757D;
  font-size: 0.98rem;
  text-align: center;
  padding: 1.2rem 0 1.2rem 0;
  margin-top: 2rem;
  border-top: 1px solid #E9ECEF;
  a {
    color: #6C757D;
    text-decoration: underline;
    margin: 0 0.7rem;
    &:hover {
      color: #4A6FA5;
    }
  }
`;

const quizFlow = [
  'landing',
  'question-1',
  'question-2',
  'intermediate-3',
  'question-4',
  'question-5',
  'intermediate-6',
  'question-7',
  'question-8',
  'question-9',
  'intermediate-10',
  'question-11',
  'question-12',
  'question-13',
  'intermediate-14',
  'question-15',
  'question-16',
  'question-17',
  'intermediate-18',
  'question-19',
  'analysis',
  'results',
  'offer'
];

const QuizContainer = () => {
  const [currentScreen, setCurrentScreen] = useState('landing');
  const [answers, setAnswers] = useState({});
  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const totalQuestions = quizFlow.filter(id => id.startsWith('question-') || id.startsWith('intermediate-')).length;

  const handleStartQuiz = () => {
    setCurrentScreen('question-1');
    setProgress(1);
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));

    // Determine next screen
    const nextScreen = getNextScreen(currentScreen);
    setCurrentScreen(nextScreen);
    updateProgress(nextScreen);
  };

  const getNextScreen = (currentId) => {
    const idx = quizFlow.indexOf(currentId);
    if (idx === -1 || idx === quizFlow.length - 1) return 'results';
    return quizFlow[idx + 1];
  };

  const updateProgress = (screenId) => {
    const idx = quizFlow.findIndex(id => id === screenId);
    const completed = quizFlow.slice(0, idx + 1).filter(id => id.startsWith('question-') || id.startsWith('intermediate-')).length;
    setProgress((completed / totalQuestions) * 100);
  };

  const handleIntermediateContinue = (screenId) => {
    const nextScreen = getNextScreen(screenId);
    setCurrentScreen(nextScreen);
    updateProgress(nextScreen);
  };

  const handleAnalysisComplete = () => {
    setCurrentScreen('results');
  };

  const handleWeiter = () => {
    setCurrentScreen('offer');
  };

  const renderScreen = () => {
    switch (currentScreen) {
      case 'landing':
        return <LandingPage onStart={handleStartQuiz} />;
      case 'analysis':
        return <AnalysisScreen onComplete={handleAnalysisComplete} />;
      case 'results':
        return <ResultsScreen answers={answers} onWeiter={handleWeiter} />;
      case 'offer':
        return <OfferScreen />;
      default:
        if (currentScreen.startsWith('intermediate')) {
          return (
            <IntermediateScreen
              screenId={currentScreen}
              onContinue={handleIntermediateContinue}
            />
          );
        }
        return (
          <QuestionScreen
            questionId={currentScreen}
            onAnswer={handleAnswer}
            progress={progress}
          />
        );
    }
  };

  return (
    <>
      <Container>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentScreen}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderScreen()}
          </motion.div>
        </AnimatePresence>
      </Container>
      {/* Sitewide Footer */}
      <Footer>
        <a href="https://dermapuris.de/impressum/" target="_blank" rel="noopener noreferrer">Impressum</a>
        <a href="https://dermapuris.de/zahlung-und-versand/" target="_blank" rel="noopener noreferrer">Zahlung und Versand</a>
        <a href="https://dermapuris.de/agb/" target="_blank" rel="noopener noreferrer">AGB</a>
        <a href="https://dermapuris.de/datenschutzerklaerung/" target="_blank" rel="noopener noreferrer">Datenschutzerklärung</a>
        <a href="https://dermapuris.de/widerrufsrecht/" target="_blank" rel="noopener noreferrer">Widerrufsrecht</a>
      </Footer>
    </>
  );
};

export default QuizContainer; 